import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import { getMyStore, getStoreHours, updateStoreHours } from '../../services/supabaseService';

const DAYS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];
const DAY_ORDER = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

export const StoreHours: React.FC = () => {
    const { user } = useAuth();
    const { showNotification } = useNotification();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [store, setStore] = useState<any>(null);

    const [operatingHours, setOperatingHours] = useState<Record<string, { open: string; close: string; closed: boolean }>>({
        'Segunda': { open: '09:00', close: '18:00', closed: false },
        'Terça': { open: '09:00', close: '18:00', closed: false },
        'Quarta': { open: '09:00', close: '18:00', closed: false },
        'Quinta': { open: '09:00', close: '18:00', closed: false },
        'Sexta': { open: '09:00', close: '18:00', closed: false },
        'Sábado': { open: '09:00', close: '14:00', closed: false },
        'Domingo': { open: '00:00', close: '00:00', closed: true },
    });

    useEffect(() => {
        if (user && user.role_id === 6) {
            fetchData();
        } else if (user) {
            navigate('/');
        }
    }, [user]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const myStore = await getMyStore();
            if (myStore) {
                setStore(myStore);
                const hoursData = await getStoreHours(myStore.id);
                if (hoursData && hoursData.length > 0) {
                    const loadedHours: Record<string, { open: string; close: string; closed: boolean }> = {};
                    DAYS.forEach(day => {
                        loadedHours[day] = { open: '09:00', close: '18:00', closed: true };
                    });
                    hoursData.forEach((item: any) => {
                        const dayName = DAY_ORDER[item.day_of_week];
                        if (dayName) {
                            loadedHours[dayName] = {
                                open: item.open_time ? item.open_time.substring(0, 5) : '09:00',
                                close: item.close_time ? item.close_time.substring(0, 5) : '18:00',
                                closed: !!item.is_closed
                            };
                        }
                    });
                    setOperatingHours(loadedHours);
                }
            }
        } catch (error) {
            showNotification("Erro ao carregar horários de atendimento", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveHours = async () => {
        if (!store) return;
        setSaving(true);
        try {
            const hoursPayload = DAYS.map(day => {
                const day_of_week = DAY_ORDER.indexOf(day);
                const info = operatingHours[day];
                return {
                    day_of_week,
                    open_time: info.closed ? null : (info.open ? `${info.open}:00` : null),
                    close_time: info.closed ? null : (info.close ? `${info.close}:00` : null),
                    is_closed: !!info.closed
                };
            });
            const success = await updateStoreHours(store.id, hoursPayload);
            if (success) {
                showNotification("Horários de atendimento atualizados com sucesso!", "success");
            } else {
                showNotification("Erro ao salvar horários de atendimento", "error");
            }
        } catch (error) {
            showNotification("Erro ao salvar horários", "error");
        } finally {
            setSaving(false);
        }
    };

    const applyCommercialPreset = () => {
        setOperatingHours({
            'Segunda': { open: '09:00', close: '18:00', closed: false },
            'Terça': { open: '09:00', close: '18:00', closed: false },
            'Quarta': { open: '09:00', close: '18:00', closed: false },
            'Quinta': { open: '09:00', close: '18:00', closed: false },
            'Sexta': { open: '09:00', close: '18:00', closed: false },
            'Sábado': { open: '09:00', close: '14:00', closed: false },
            'Domingo': { open: '00:00', close: '00:00', closed: true },
        });
        showNotification("Horário comercial padrão aplicado (lembre-se de salvar)", "info");
    };

    if (loading) {
        return (
            <div className="p-12 text-center text-slate-500 font-bold uppercase tracking-widest text-xs animate-pulse">
                Carregando horários...
            </div>
        );
    }

    if (!store) {
        return (
            <div className="p-12 text-center text-slate-400 font-bold text-sm">
                Loja não encontrada ou você não tem permissão para gerenciar esta loja.
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in duration-300">
            {/* Header */}
            <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-800">
                <div>
                    <div className="flex items-center gap-2 text-xs font-bold text-slate-400 mb-1">
                        <Link to="/minha-loja" className="hover:text-purple-400 transition-colors">Configurações</Link>
                        <span>/</span>
                        <span className="text-white">Horários de Atendimento</span>
                    </div>
                    <h1 className="text-2xl font-black text-white uppercase tracking-tight flex items-center gap-3">
                        <i className="fas fa-clock text-blue-500 text-xl"></i>
                        Horários de Atendimento
                    </h1>
                    <p className="text-slate-400 text-xs mt-0.5">
                        Defina os dias e turnos em que a loja física ou suporte online está aberto ao público.
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={applyCommercialPreset}
                        className="px-3.5 py-2 bg-slate-800/80 hover:bg-slate-800 text-slate-300 hover:text-white rounded-xl text-xs font-bold border border-slate-700/60 transition-all flex items-center gap-2"
                        title="Preencher rapidamente de segunda a sábado"
                    >
                        <i className="fas fa-wand-magic-sparkles text-xs text-blue-400"></i>
                        Padrão Comercial
                    </button>
                    <button
                        onClick={handleSaveHours}
                        disabled={saving}
                        className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-black px-5 py-2 rounded-xl text-xs uppercase tracking-wider transition-all shadow-md shadow-blue-600/20 active:scale-95 flex items-center gap-2"
                    >
                        {saving ? (
                            <>
                                <i className="fas fa-spinner fa-spin text-xs"></i>
                                Salvando...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-check text-xs"></i>
                                Salvar Horários
                            </>
                        )}
                    </button>
                </div>
            </header>

            {/* Main Card */}
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 sm:p-7 space-y-5">
                <div className="flex items-center justify-between pb-3 border-b border-slate-800/80">
                    <span className="text-[11px] font-black uppercase tracking-wider text-slate-400">Dia da Semana</span>
                    <span className="text-[11px] font-black uppercase tracking-wider text-slate-400">Expediente</span>
                </div>

                <div className="space-y-3">
                    {DAYS.map(day => {
                        const isClosed = operatingHours[day]?.closed;
                        return (
                            <div 
                                key={day} 
                                className={`flex flex-col sm:flex-row sm:items-center justify-between p-3.5 sm:p-4 rounded-xl border transition-all ${
                                    isClosed 
                                        ? 'bg-slate-950/30 border-slate-800/60 opacity-80' 
                                        : 'bg-slate-950/60 border-slate-800 hover:border-slate-700'
                                }`}
                            >
                                <div className="flex items-center justify-between sm:justify-start space-x-4 mb-2 sm:mb-0">
                                    <div className="w-28 text-xs font-bold text-white flex items-center gap-2">
                                        <span className={`w-2 h-2 rounded-full ${isClosed ? 'bg-slate-600' : 'bg-emerald-400'}`}></span>
                                        {day}
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input 
                                            type="checkbox" 
                                            className="sr-only peer"
                                            checked={!isClosed}
                                            onChange={(e) => setOperatingHours({
                                                ...operatingHours,
                                                [day]: { ...operatingHours[day], closed: !e.target.checked }
                                            })}
                                        />
                                        <div className="w-9 h-5 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                                        <span className="ml-2 text-[11px] text-slate-400 font-medium">
                                            {!isClosed ? 'Aberto' : 'Fechado'}
                                        </span>
                                    </label>
                                </div>

                                {!isClosed ? (
                                    <div className="flex items-center space-x-2 self-end sm:self-auto">
                                        <input 
                                            type="time" 
                                            className="bg-slate-900 border border-slate-800 rounded-lg px-2.5 py-1 text-xs text-white color-scheme-dark focus:border-blue-500 focus:outline-none"
                                            value={operatingHours[day]?.open || '09:00'}
                                            onChange={(e) => setOperatingHours({
                                                ...operatingHours,
                                                [day]: { ...operatingHours[day], open: e.target.value }
                                            })}
                                        />
                                        <span className="text-slate-500 text-xs font-black">às</span>
                                        <input 
                                            type="time" 
                                            className="bg-slate-900 border border-slate-800 rounded-lg px-2.5 py-1 text-xs text-white color-scheme-dark focus:border-blue-500 focus:outline-none"
                                            value={operatingHours[day]?.close || '18:00'}
                                            onChange={(e) => setOperatingHours({
                                                ...operatingHours,
                                                [day]: { ...operatingHours[day], close: e.target.value }
                                            })}
                                        />
                                    </div>
                                ) : (
                                    <span className="text-[10px] font-black text-rose-400 uppercase tracking-wider px-2.5 py-1 bg-rose-500/10 rounded-lg border border-rose-500/20 self-end sm:self-auto">
                                        Não abre
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="pt-4 border-t border-slate-800 flex justify-end">
                    <button 
                        onClick={handleSaveHours}
                        disabled={saving}
                        className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-black px-8 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all shadow-md shadow-blue-600/20 active:scale-95 flex items-center gap-2"
                    >
                        {saving ? 'Salvando...' : 'Salvar Alterações'}
                    </button>
                </div>
            </div>
        </div>
    );
};
